import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import {
  ReportTableProps,
  StudentOutstandingDetail,
  ClassWiseTotal,
  GrandTotal,
  FlatRow
} from './types';
import {
  classKey,
  extractParticularKeys,
  formatNumber,
  buildClassGroups,
  SCHOOLS_EXCLUDING_BOUNCE_CHARGES
} from './utils';
import {
  reportDataGridSx,
  NumericCell,
  TotalCell,
  ClassHeaderContainer,
  SecondaryText,
  LabelCell,
  TableWrapper
} from './styled';
import CustomDataGridToolbar from '../common/components/CustomDataGridToolbar';

const buildRows = (
  studentRows: StudentOutstandingDetail[],
  classWiseTotal: ClassWiseTotal[],
  grandTotal: GrandTotal,
  particularKeys: string[]
): FlatRow[] => {
  const classGroups = buildClassGroups(studentRows, classWiseTotal);
  const rows: FlatRow[] = [];

  classGroups.forEach(({ classTotal, students }) => {
    const key = classKey(classTotal);

    rows.push({
      id: `header-${key}`,
      _rowType: 'class-header',
      _label: classTotal.division
        ? `Class ${classTotal.grade} - ${classTotal.division}`
        : `Class ${classTotal.grade} (Students with unassigned divisions)`,
      rollNo: '',
      name: '',
      lateFee: '',
      chequeBounceFee: '',
      total: '',
      ...Object.fromEntries(particularKeys.map((k) => [k, '']))
    });

    students.forEach((student) => {
      rows.push({
        id: `${key}|||${student.studentId}`,
        _rowType: 'student',
        _label: '',
        rollNo: student.rollNo,
        name: student.name,
        lateFee: student.lateFee,
        chequeBounceFee: student.chequeBounceFee,
        total: student.total,
        ...Object.fromEntries(
          particularKeys.map((k) => [k, student.particulars[k] ?? 0])
        )
      });
    });

    rows.push({
      id: `subtotal-${key}`,
      _rowType: 'subtotal',
      _label: 'Total',
      rollNo: '',
      name: '',
      lateFee: classTotal.lateFeeTotal,
      chequeBounceFee: classTotal.chequeBounceFeeTotal,
      total: classTotal.total,
      ...Object.fromEntries(
        particularKeys.map((k) => [k, classTotal.particularTotals[k] ?? 0])
      )
    });

    rows.push({
      id: `spacer-${key}`,
      _rowType: 'spacer',
      _label: '',
      rollNo: '',
      name: '',
      lateFee: '',
      chequeBounceFee: '',
      total: '',
      ...Object.fromEntries(particularKeys.map((k) => [k, '']))
    });
  });

  rows.push({
    id: 'grand-total',
    _rowType: 'grand-total',
    _label: 'Grand Total',
    rollNo: '',
    name: '',
    lateFee: grandTotal.lateFeeTotal,
    chequeBounceFee: grandTotal.chequeBounceFeeTotal,
    total: grandTotal.total,
    ...Object.fromEntries(
      particularKeys.map((k) => [k, grandTotal.particularTotals[k] ?? 0])
    )
  });

  return rows;
};

const numericRenderCell = (params: GridRenderCellParams) => {
  if (
    params.row._rowType === 'class-header' ||
    params.row._rowType === 'spacer' ||
    params.value === ''
  )
    return null;
  return <NumericCell>{formatNumber(params.value as number)}</NumericCell>;
};

const buildColumns = (
  particularKeys: string[],
  school: string
): GridColDef[] => {
  const columns: GridColDef[] = [
    {
      field: '_label',
      headerName: '',
      width: 200,
      sortable: false,
      renderCell: (params) => {
        if (
          params.row._rowType === 'class-header' &&
          typeof params.value === 'string' &&
          params.value.includes('(Students with unassigned divisions)')
        ) {
          const classLabel = params.value.replace(
            ' (Students with unassigned divisions)',
            ''
          );
          return (
            <ClassHeaderContainer>
              <Box>{classLabel}</Box>
              <SecondaryText>
                (Students with unassigned divisions)
              </SecondaryText>
            </ClassHeaderContainer>
          );
        }
        return (
          <LabelCell isBold={params.row._rowType !== 'student'}>
            {params.value as string}
          </LabelCell>
        );
      }
    },
    {
      field: 'rollNo',
      headerName: 'Roll No',
      width: 120,
      sortable: false,
      renderCell: (params) => <Box>{params.value}</Box>
    },
    {
      field: 'name',
      headerName: 'Student Name',
      width: 200,
      sortable: false
    },
    ...particularKeys.map((k) => ({
      field: k,
      headerName: k,
      width: 140,
      sortable: false,
      headerAlign: 'right' as const,
      renderCell: numericRenderCell
    })),
    {
      field: 'lateFee',
      headerName: 'Late Fee',
      width: 120,
      sortable: false,
      headerAlign: 'right',
      renderCell: numericRenderCell
    }
  ];

  if (!SCHOOLS_EXCLUDING_BOUNCE_CHARGES.includes(school)) {
    columns.push({
      field: 'chequeBounceFee',
      headerName: 'Bounce Charges',
      width: 150,
      sortable: false,
      headerAlign: 'right',
      renderCell: numericRenderCell
    });
  }

  columns.push({
    field: 'total',
    headerName: 'Total',
    width: 130,
    sortable: false,
    headerAlign: 'right',
    renderCell: (params: GridRenderCellParams) => {
      if (params.row._rowType === 'class-header' || params.value === '')
        return null;
      return <TotalCell>{formatNumber(params.value as number)}</TotalCell>;
    }
  });

  return columns;
};

const OutstandingFeeByClassReportTable: React.FC<ReportTableProps> = ({
  studentRows,
  classWiseTotal,
  grandTotal,
  school
}) => {
  const particularKeys = useMemo(
    () => extractParticularKeys(studentRows),
    [studentRows]
  );

  const columns = useMemo(
    () => buildColumns(particularKeys, school),
    [particularKeys, school]
  );

  const rows = useMemo(
    () => buildRows(studentRows, classWiseTotal, grandTotal, particularKeys),
    [studentRows, classWiseTotal, grandTotal, particularKeys]
  );

  return (
    <TableWrapper>
      <DataGrid
        autoHeight
        rows={rows}
        columns={columns}
        disableRowSelectionOnClick
        showToolbar
        pagination
        pageSizeOptions={[25, 50, 100]}
        initialState={{
          pagination: { paginationModel: { pageSize: 25, page: 0 } }
        }}
        slots={{ toolbar: CustomDataGridToolbar }}
        slotProps={{
          toolbar: {
            printOptions: { disableToolbarButton: true }
          }
        }}
        getRowClassName={(params) => params.row._rowType as string}
        getRowHeight={(params) =>
          params.model._rowType === 'class-header' &&
          typeof params.model._label === 'string' &&
          params.model._label.includes('Students with unassigned divisions')
            ? 64
            : null
        }
        sx={reportDataGridSx}
      />
    </TableWrapper>
  );
};

export default OutstandingFeeByClassReportTable;
